import { motion } from "motion/react";
import { navSteps } from "@/types/ui";

type LearningPathNavProps = {
  activeStep?: number;
  compact?: boolean;
  onStepChange?: (step: number) => void;
};

type LearningPathStepProps = {
  compact: boolean;
  description: string;
  index: number;
  isActive: boolean;
  isClickable: boolean;
  label: string;
  onStepChange?: (step: number) => void;
  showConnector: boolean;
};

const stepDescriptions = [
  "Explore markers",
  "Select a city",
  "See today's timings",
  "Switch presets",
  "Compare timezones",
  "Drive camera focus",
] as const;

const getContainerClassName = (compact: boolean) =>
  compact
    ? "rounded-xl border border-zinc-800/50 bg-zinc-950/80 px-1.5 py-1.5 backdrop-blur-md"
    : "rounded-2xl border border-zinc-800/50 bg-zinc-950/80 px-2 py-2 md:px-5 md:py-3 backdrop-blur-md";

const getStepButtonClassName = (isClickable: boolean) => `group ${isClickable ? "cursor-pointer" : "cursor-default"}`;

const getStepWrapperClassName = (compact: boolean, isClickable: boolean) =>
  `flex items-center gap-2 ${compact ? "px-1 py-1" : "px-2 py-2.5 md:py-1"} ${isClickable ? "cursor-pointer" : ""}`;

const getStepBadgeClassName = (compact: boolean, isActive: boolean) =>
  `flex items-center justify-center rounded-full font-semibold transition-colors ${
    compact ? "h-6 w-6 text-[9px]" : "h-7 w-7 text-[10px] md:h-6 md:w-6"
  } ${
    isActive
      ? "border border-emerald-500/50 bg-emerald-400/10 text-emerald-400"
      : "border border-zinc-700 text-zinc-500 hover:border-zinc-600 hover:text-zinc-400"
  }`;

const LearningPathStep = ({
  compact,
  description,
  index,
  isActive,
  isClickable,
  label,
  onStepChange,
  showConnector,
}: LearningPathStepProps) => (
  <div className="flex items-center">
    <button
      className={getStepButtonClassName(isClickable)}
      onClick={isClickable ? () => onStepChange?.(index) : undefined}
      type="button"
    >
      <div className={getStepWrapperClassName(compact, isClickable)}>
        <div className={getStepBadgeClassName(compact, isActive)}>{index + 1}</div>
        {!compact && (
          <div className="hidden sm:block">
            <p className={`text-xs font-medium transition-colors ${isActive ? "text-zinc-200" : "text-zinc-500"}`}>
              {label}
            </p>
            <p className="hidden text-[10px] text-zinc-700 lg:block">{description}</p>
          </div>
        )}
      </div>
    </button>
    {showConnector && (
      <div className={`h-px border-t border-dashed border-zinc-800 ${compact ? "w-1" : "w-2 md:w-4"}`} />
    )}
  </div>
);

const LearningPathNav = ({ activeStep = 0, compact = false, onStepChange }: LearningPathNavProps) => (
  <motion.div
    animate={{ opacity: 1, y: 0 }}
    className={getContainerClassName(compact)}
    initial={{ opacity: 0, y: 16 }}
    transition={{ delay: 0.8, duration: 0.5 }}
  >
    <div className={`flex items-center ${compact ? "gap-0" : "gap-1"}`}>
      {navSteps.map((step, index) => (
        <LearningPathStep
          compact={compact}
          description={stepDescriptions[index] ?? ""}
          index={index}
          isActive={index === activeStep}
          isClickable={!onStepChange ? false : index !== activeStep}
          key={step}
          label={step}
          onStepChange={onStepChange}
          showConnector={index < navSteps.length - 1}
        />
      ))}
    </div>
  </motion.div>
);

export default LearningPathNav;
